import React, { useState, useEffect, useRef } from "react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ExternalLink,
  Maximize2,
  Download,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FileCode2,
  Image as ImageIcon,
  Move,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { toPng, toJpeg } from "html-to-image"

interface FullscreenMediaModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  src?: string | null
  alt?: string
  children?: React.ReactNode
  allowDownload?: boolean
  downloadFilename?: string
}

function triggerDownload(href: string, filename: string) {
  const a = document.createElement("a")
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

async function exportSvgToRaster(
  svg: SVGElement,
  filename: string,
  format: "png" | "jpeg" = "png"
): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const clone = svg.cloneNode(true) as SVGElement
      if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg")
      }
      if (!clone.getAttribute("xmlns:xlink")) {
        clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink")
      }

      const rect = svg.getBoundingClientRect()
      const viewBox = svg.getAttribute("viewBox")
      let width = Math.max(Math.round(rect.width), 320)
      let height = Math.max(Math.round(rect.height), 120)
      if (viewBox) {
        const parts = viewBox.split(/[\s,]+/).map(Number)
        if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
          width = parts[2]
          height = parts[3]
        }
      }
      clone.setAttribute("width", `${width}`)
      clone.setAttribute("height", `${height}`)

      const svgData = new XMLSerializer().serializeToString(clone)
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
      const blobUrl = URL.createObjectURL(svgBlob)

      const img = new Image()
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas")
          const scale = 2
          canvas.width = width * scale
          canvas.height = height * scale
          const ctx = canvas.getContext("2d")
          if (ctx) {
            if (format === "jpeg") {
              ctx.fillStyle = "#ffffff"
              ctx.fillRect(0, 0, canvas.width, canvas.height)
            }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            const mime = format === "jpeg" ? "image/jpeg" : "image/png"
            const dataUrl = canvas.toDataURL(mime, 0.95)
            const ext = format === "jpeg" ? "jpg" : "png"
            triggerDownload(dataUrl, `${filename}.${ext}`)
            URL.revokeObjectURL(blobUrl)
            resolve(true)
            return
          }
        } catch {
          // Canvas export fallback
        }
        triggerDownload(blobUrl, `${filename}.svg`)
        URL.revokeObjectURL(blobUrl)
        resolve(true)
      }
      img.onerror = () => {
        triggerDownload(blobUrl, `${filename}.svg`)
        URL.revokeObjectURL(blobUrl)
        resolve(true)
      }
      img.src = blobUrl
    } catch {
      resolve(false)
    }
  })
}

export async function downloadMedia({
  url,
  element,
  filename = "diagram",
  format = "png",
}: {
  url?: string | null
  element?: HTMLElement | null
  filename?: string
  format?: "png" | "svg" | "jpeg"
}): Promise<void> {
  const baseName = filename.toLowerCase().replace(/[^a-z0-9_-]+/g, "-")

  try {
    // 1. Format: Direct Vector SVG Export
    if (format === "svg") {
      if (element) {
        const svg =
          element.tagName.toLowerCase() === "svg"
            ? (element as unknown as SVGElement)
            : element.querySelector("svg")

        if (svg) {
          const clone = svg.cloneNode(true) as SVGElement
          if (!clone.getAttribute("xmlns")) {
            clone.setAttribute("xmlns", "http://www.w3.org/2000/svg")
          }
          if (!clone.getAttribute("xmlns:xlink")) {
            clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink")
          }

          // 1. Remove overflow: hidden so browsers enable document scrolling when opened standalone
          clone.style.overflow = "visible"

          // 2. Normalize width and height from viewBox so it fits natural aspect ratio instead of
          // blowing up to 100% viewport width and pushing the bottom off screen
          const viewBox = clone.getAttribute("viewBox")
          if (viewBox) {
            const parts = viewBox.split(/[\s,]+/).map(Number)
            if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
              const vbWidth = parts[2]
              const vbHeight = parts[3]
              clone.setAttribute("width", `${vbWidth}`)
              clone.setAttribute("height", `${vbHeight}`)
            }
          }

          // 3. Remove inline width: 100% style which caused horizontal stretching & bottom cropping
          clone.style.removeProperty("width")
          clone.style.removeProperty("height")
          clone.style.removeProperty("max-width")

          // Preserve any container-level CSS <style> definitions inside the SVG
          const styles = element.querySelectorAll("style")
          if (styles.length > 0) {
            let combinedStyles = ""
            styles.forEach((st) => {
              if (!clone.contains(st)) {
                combinedStyles += (st.textContent || "") + "\n"
              }
            })
            if (combinedStyles.trim()) {
              let defs = clone.querySelector("defs")
              if (!defs) {
                defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
                clone.insertBefore(defs, clone.firstChild)
              }
              const newStyle = document.createElementNS("http://www.w3.org/2000/svg", "style")
              newStyle.textContent = combinedStyles
              defs.appendChild(newStyle)
            }
          }

          const svgData = new XMLSerializer().serializeToString(clone)
          const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" })
          const blobUrl = window.URL.createObjectURL(svgBlob)
          triggerDownload(blobUrl, `${baseName}.svg`)
          window.URL.revokeObjectURL(blobUrl)
          return
        }
      }

      if (url && url.toLowerCase().includes(".svg")) {
        try {
          const res = await fetch(url, { mode: "cors" })
          if (res.ok) {
            const blob = await res.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            triggerDownload(blobUrl, `${baseName}.svg`)
            window.URL.revokeObjectURL(blobUrl)
            return
          }
        } catch {
          // Fallback to direct URL
        }
        triggerDownload(url, `${baseName}.svg`)
        return
      }
    }

    // 2. Format: JPEG Export
    if (format === "jpeg" && element) {
      const svg =
        element.tagName.toLowerCase() === "svg"
          ? (element as unknown as SVGElement)
          : element.querySelector("svg")

      if (svg) {
        const exported = await exportSvgToRaster(svg, baseName, "jpeg")
        if (exported) return
      }

      const originalTransform = element.style.transform
      element.style.transform = "none"
      try {
        const dataUrl = await toJpeg(element, {
          quality: 0.95,
          backgroundColor: "#070d1a",
          pixelRatio: 2,
          skipFonts: true,
          fontEmbedCSS: "",
        })
        triggerDownload(dataUrl, `${baseName}.jpg`)
        element.style.transform = originalTransform
        return
      } catch (e) {
        console.warn("toJpeg export failed, falling back to PNG:", e)
      } finally {
        element.style.transform = originalTransform
      }
    }

    // 3. Format: High-Res PNG Export (Default)
    if (url) {
      try {
        const res = await fetch(url, { mode: "cors" })
        if (res.ok) {
          const blob = await res.blob()
          const blobUrl = window.URL.createObjectURL(blob)
          const ext = url.split(".").pop()?.split("?")[0] || "png"
          triggerDownload(blobUrl, `${baseName}.${ext}`)
          window.URL.revokeObjectURL(blobUrl)
          return
        }
      } catch {
        // Cross-origin fallback
      }
      triggerDownload(url, `${baseName}.png`)
    } else if (element) {
      const svg =
        element.tagName.toLowerCase() === "svg"
          ? (element as unknown as SVGElement)
          : element.querySelector("svg")

      if (svg) {
        const exported = await exportSvgToRaster(svg, baseName, "png")
        if (exported) return
      }

      const originalTransform = element.style.transform
      element.style.transform = "none"

      let downloaded = false

      // Primary: try toPng with skipFonts: true and empty fontEmbedCSS to avoid stylesheet crawling errors
      try {
        const dataUrl = await toPng(element, {
          skipFonts: true,
          fontEmbedCSS: "",
          cacheBust: false,
          imagePlaceholder: "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E",
          backgroundColor: "#070d1a",
          pixelRatio: 2,
        })
        triggerDownload(dataUrl, `${baseName}.png`)
        downloaded = true
      } catch (toPngErr) {
        console.warn("toPng export encountered an issue, attempting direct SVG/Canvas export:", toPngErr)
      }

      // Fallback: If the element contains an <img> tag (e.g. from prod ActiveStorage)
      if (!downloaded) {
        const img = element.querySelector("img")
        if (img?.src) {
          triggerDownload(img.src, `${baseName}.png`)
          downloaded = true
        }
      }

      element.style.transform = originalTransform
    }
  } catch (err) {
    console.error("Failed to download media:", err)
  }
}

export function FullscreenMediaModal({
  isOpen,
  onClose,
  title = "Media Viewer",
  src,
  alt = "Fullscreen media",
  children,
  allowDownload = false,
  downloadFilename,
}: FullscreenMediaModalProps) {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const lastMouseRef = useRef({ x: 0, y: 0 })
  const lastTouchRef = useRef({ x: 0, y: 0 })
  const [downloading, setDownloading] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  // Reset scale and position when dialog opens or closes
  useEffect(() => {
    if (isOpen) {
      setScale(1)
      setPosition({ x: 0, y: 0 })
      setIsDragging(false)
    }
  }, [isOpen])

  const handleZoomIn = () => setScale((prev) => Math.min(4, +(prev + 0.25).toFixed(2)))
  const handleZoomOut = () => {
    setScale((prev) => {
      const next = Math.max(0.5, +(prev - 0.25).toFixed(2))
      if (next <= 1) setPosition({ x: 0, y: 0 })
      return next
    })
  }
  const handleReset = () => {
    setScale(1)
    setPosition({ x: 0, y: 0 })
    setIsDragging(false)
  }

  // Mouse drag panning handlers with precise 1:1 incremental delta tracking
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.preventDefault()
    setIsDragging(true)
    lastMouseRef.current = { x: e.clientX, y: e.clientY }
  }

  useEffect(() => {
    if (!isDragging) return

    const handleWindowMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - lastMouseRef.current.x
      const dy = e.clientY - lastMouseRef.current.y
      lastMouseRef.current = { x: e.clientX, y: e.clientY }
      setPosition((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }))
    }

    const handleWindowMouseUp = () => {
      setIsDragging(false)
    }

    window.addEventListener("mousemove", handleWindowMouseMove)
    window.addEventListener("mouseup", handleWindowMouseUp)
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove)
      window.removeEventListener("mouseup", handleWindowMouseUp)
    }
  }, [isDragging])

  // Touch drag panning handlers with precise 1:1 incremental delta tracking
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true)
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return
    const dx = e.touches[0].clientX - lastTouchRef.current.x
    const dy = e.touches[0].clientY - lastTouchRef.current.y
    lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    setPosition((prev) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }))
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
  }

  // Wheel handling:
  // - Ctrl/Meta key: smooth zoom
  // - Normal wheel (when scale > 1): smooth trackpad/mouse scroll to pan with controlled dampening
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const delta = e.deltaY < 0 ? 0.2 : -0.2
      setScale((prev) => {
        const next = Math.min(4, Math.max(0.5, +(prev + delta).toFixed(2)))
        if (next <= 1) setPosition({ x: 0, y: 0 })
        return next
      })
    } else if (scale > 1) {
      e.preventDefault()
      const factor = 0.6
      setPosition((prev) => ({
        x: Math.round(prev.x - e.deltaX * factor),
        y: Math.round(prev.y - e.deltaY * factor),
      }))
    }
  }

  // Keyboard navigation for panning and zooming
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (["INPUT", "TEXTAREA"].includes((e.target as HTMLElement)?.tagName)) return

      const STEP = 40
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setPosition((prev) => ({ ...prev, y: prev.y + STEP }))
      } else if (e.key === "ArrowDown") {
        e.preventDefault()
        setPosition((prev) => ({ ...prev, y: prev.y - STEP }))
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        setPosition((prev) => ({ ...prev, x: prev.x + STEP }))
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        setPosition((prev) => ({ ...prev, x: prev.x - STEP }))
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault()
        handleZoomIn()
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault()
        handleZoomOut()
      } else if (e.key === "0" || e.key.toLowerCase() === "r") {
        e.preventDefault()
        handleReset()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, scale])

  const handleDownload = async (format: "png" | "svg" | "jpeg" = "png") => {
    if (downloading) return
    setDownloading(true)
    const baseName =
      downloadFilename ||
      (title ? title.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "diagram-media")

    try {
      await downloadMedia({
        url: src,
        element: contentRef.current,
        filename: baseName,
        format,
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-[95vw] w-[95vw] md:max-w-6xl max-h-[92vh] h-[92vh] p-0 flex flex-col overflow-hidden bg-card border-border shadow-2xl rounded-2xl">
        {/* Top Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border/80 bg-muted/30 shrink-0">
          <DialogTitle className="text-sm font-semibold text-foreground flex items-center gap-2 truncate">
            <Maximize2 className="h-4 w-4 text-primary shrink-0" />
            <span className="truncate">{title}</span>
          </DialogTitle>

          <div className="flex items-center gap-2 mr-7">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-background/80 border border-border/60 rounded-lg p-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                title="Zoom Out (-)"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-[11px] font-mono px-1.5 text-muted-foreground min-w-[42px] text-center select-none font-medium">
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={handleZoomIn}
                disabled={scale >= 4}
                title="Zoom In (+)"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                onClick={handleReset}
                disabled={scale === 1 && position.x === 0 && position.y === 0}
                title="Reset Zoom & Position (0 / R)"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>

            {/* Multi-Format Download Dropdown */}
            {allowDownload && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-[11px] gap-1.5 text-secondary hover:text-secondary border-secondary/40 hover:bg-secondary/10 bg-background/80 font-medium"
                    disabled={downloading}
                    title="Download diagram in multiple formats"
                  >
                    {downloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    <span>{downloading ? "Exporting…" : "Download"}</span>
                    <ChevronDown className="h-3 w-3 opacity-60 ml-0.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 text-xs font-medium">
                  <DropdownMenuItem
                    onClick={() => handleDownload("svg")}
                    className="gap-2 cursor-pointer text-foreground hover:text-secondary font-medium"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-secondary" />
                    <span>Vector SVG (.svg)</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDownload("png")}
                    className="gap-2 cursor-pointer text-foreground hover:text-primary font-medium"
                  >
                    <ImageIcon className="h-3.5 w-3.5 text-primary" />
                    <span>High-Res PNG (.png)</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDownload("jpeg")}
                    className="gap-2 cursor-pointer text-foreground hover:text-foreground font-medium"
                  >
                    <FileCode2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>Compressed JPEG (.jpg)</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {src && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground bg-background/80 border-border/60"
                asChild
              >
                <a href={src} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                  <ExternalLink className="h-3 w-3" />
                  <span className="hidden sm:inline">Open Original</span>
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Media Container with smooth pan/zoom/scroll */}
        <div
          className={`flex-1 w-full overflow-hidden p-3 sm:p-6 flex items-center justify-center bg-muted/15 select-none relative touch-none ${
            isDragging ? "cursor-grabbing" : scale > 1 ? "cursor-grab" : "cursor-default"
          }`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          <div
            ref={contentRef}
            className={`${isDragging ? "transition-none" : "transition-transform duration-150 ease-out"} origin-center flex items-center justify-center w-full max-w-6xl pointer-events-auto select-none`}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            }}
          >
            {src ? (
              <img
                src={src}
                alt={alt}
                draggable={false}
                className="max-h-[78vh] w-auto max-w-full object-contain rounded-xl border border-border/40 shadow-sm pointer-events-none select-none"
              />
            ) : children ? (
              <div className="w-full flex items-center justify-center pointer-events-auto select-none [&_img]:pointer-events-none [&_img]:select-none [&>div]:!max-w-none [&>div]:!w-full [&>div>div]:!max-w-none [&>div>div]:!w-full [&_svg]:!w-full [&_svg]:!max-h-[78vh] [&_svg]:!h-auto">
                {children}
              </div>
            ) : null}
          </div>

          {/* Directional Pan & Reset Helper Pill */}
          {scale > 1 && (
            <div className="absolute bottom-4 z-20 flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/95 border border-border/80 text-[11px] text-muted-foreground shadow-sm backdrop-blur-xs animate-in fade-in-50 select-none">
              <Move className="h-3.5 w-3.5 text-primary" />
              <span>Drag, scroll, or arrows to pan</span>
              <span className="text-border">|</span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setPosition((p) => ({ ...p, x: p.x + 40 }))}
                  className="p-1 hover:bg-muted rounded text-foreground hover:text-primary transition-colors cursor-pointer"
                  title="Pan Left (Left Arrow)"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPosition((p) => ({ ...p, y: p.y + 40 }))}
                  className="p-1 hover:bg-muted rounded text-foreground hover:text-primary transition-colors cursor-pointer"
                  title="Pan Up (Up Arrow)"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPosition((p) => ({ ...p, y: p.y - 40 }))}
                  className="p-1 hover:bg-muted rounded text-foreground hover:text-primary transition-colors cursor-pointer"
                  title="Pan Down (Down Arrow)"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setPosition((p) => ({ ...p, x: p.x - 40 }))}
                  className="p-1 hover:bg-muted rounded text-foreground hover:text-primary transition-colors cursor-pointer"
                  title="Pan Right (Right Arrow)"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <span className="text-border">|</span>
              <button
                type="button"
                onClick={handleReset}
                className="text-primary hover:underline font-medium cursor-pointer"
              >
                Reset View
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ZoomableImageProps {
  src: string
  alt?: string
  className?: string
  containerClassName?: string
  title?: string
  caption?: string
  allowDownload?: boolean
  downloadFilename?: string
}

export function ZoomableImage({
  src,
  alt = "Question Image",
  className = "max-h-72 w-auto max-w-full object-contain rounded-lg border border-border/50",
  containerClassName = "my-3 p-3 rounded-xl border border-border/70 bg-muted/20 flex flex-col items-center",
  title,
  caption,
  allowDownload = false,
  downloadFilename,
}: ZoomableImageProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const handleDirectDownload = async () => {
    if (downloading) return
    setDownloading(true)
    const baseName =
      downloadFilename ||
      (title ? title.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "question-image")

    try {
      await downloadMedia({
        url: src,
        filename: baseName,
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <div
        className={`group relative cursor-pointer select-none transition-all hover:border-primary/40 ${containerClassName}`}
        onClick={() => setIsOpen(true)}
        title="Click to view full screen"
      >
        <img src={src} alt={alt} className={className} />

        {/* Hover overlay hint */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1.5">
          {allowDownload && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleDirectDownload()
              }}
              className="bg-background/90 hover:bg-background backdrop-blur-xs border border-border px-2 py-1 rounded-md text-[11px] font-medium text-foreground flex items-center gap-1 shadow-sm transition-colors cursor-pointer"
              title="Download image (Admin only)"
            >
              {downloading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Download className="h-3 w-3 text-secondary" />
              )}
              <span>{downloading ? "..." : "Download"}</span>
            </button>
          )}

          <div className="bg-background/90 backdrop-blur-xs border border-border px-2 py-1 rounded-md text-[11px] font-medium text-foreground flex items-center gap-1.5 shadow-sm">
            <Maximize2 className="h-3 w-3 text-primary" />
            <span>Full Screen</span>
          </div>
        </div>

        {caption && (
          <span className="text-[11px] text-muted-foreground mt-1.5 font-medium group-hover:text-foreground transition-colors">
            {caption}
          </span>
        )}
      </div>

      <FullscreenMediaModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        src={src}
        alt={alt}
        title={title || caption || alt}
        allowDownload={allowDownload}
        downloadFilename={downloadFilename}
      />
    </>
  )
}
